import { connect, ConnectedProps } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import Transportation from '@components/shared/Transportation';
import Location from '@components/shared/Location';
import Divider from '@components/shared/Divider';
import { getSessionStorageValues } from '@shared/sessionStorageForSiteVisitors';
import useAnonymousViewLimit from '@shared/useAnonymousViewLimit';
import ExpandableDescription from '@components/shared/ExpandableDescription';
import actions, { Thumbnail } from '@store/actions/listingPage';
import routes from '@root/routes';
import { hasCurrentUser } from '@store/selectors';
import Breadcrumbs from '@components/shared/Breadcrumbs';
import { Listing, StoreState } from '@root/types';
import { OnlyInDesktopTablet, OnlyInMobile, ContactChooser } from '@components/shared';
import { PARAMETERS } from '@root/tracking/constants';
import Bartender from '@components/shared/Bartender';
import { useState } from 'react';
import ListingLocation from './ListingLocation';
import BasicInfo from './BasicInfo';
import SpaceProvider from './SpaceProvider';
import Amenities from './Amenities';
import Financials from './Financials';
import Gallery from './Gallery';
import AboutBuilding from './AboutBuilding';
import Rooms from './Rooms';
import SignUpModal from './SignUpModal';
import VideoTour from './VideoTour';
import s from './Layout.module.less';

export type PassedProps = {
  listing: Listing;
  isShare?: boolean;
  isPreview?: boolean;
  breadcrumbs?: boolean;
};

const mapState = (state: StoreState) => ({
  userIsLoggedIn: hasCurrentUser(state),
});

const videoSourceContent = {
  unset: PARAMETERS.spotlightTour,
  video: PARAMETERS.spaceVideo,
  three_d_tour: PARAMETERS.space3DTour,
};

const mapDispatch = {
  brandPageLinkClicked: () => actions.brandPageLinkClicked(),
  contactClicked: () => actions.contactClicked(),
  galleryPaginationClicked: () => actions.galleryPaginationClicked(),
  galleryThumbnailClicked: (thumbnailType: keyof Thumbnail = 'clickThumbnail') =>
    actions.galleryThumbnailClicked(thumbnailType),
  leaseTermChanged: () => actions.leaseTermChanged(),
  leaseTermDropdownOpened: () => actions.leaseTermDropdownOpened(),
  lightboxFloorPlanClicked: () => actions.lightboxFloorPlanClicked(),
  lightboxOpened: () => actions.lightboxOpened(),
  lightboxPaginationClicked: () => actions.lightboxPaginationClicked(),
  lightboxThumbnailClicked: () => actions.lightboxThumbnailClicked(),
  viewBuildingClicked: () => actions.viewBuildingClicked(),
};

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

const Layout = ({
  brandPageLinkClicked,
  contactClicked,
  galleryPaginationClicked,
  galleryThumbnailClicked,
  isShare = false,
  leaseTermChanged,
  leaseTermDropdownOpened,
  lightboxOpened,
  listing,
  userIsLoggedIn,
  viewBuildingClicked,
  isPreview,
  breadcrumbs = true,
}: PassedProps & ReduxProps) => {
  const { t } = useTranslation('listing');
  const flags = useFlags();

  const { arrivedOnMarketLink } = getSessionStorageValues();
  const { limitExceeded: signUpRequired } = useAnonymousViewLimit(
    userIsLoggedIn || isShare || isPreview || arrivedOnMarketLink,
    listing.id,
  );

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  return (
    <>
      {flags['move-breadcrumbs-to-layout'] && breadcrumbs ? (
        <OnlyInDesktopTablet>
          <Breadcrumbs className={s.breadcrumbs} />
        </OnlyInDesktopTablet>
      ) : null}
      <Bartender
        drinks={[
          {
            url: routes.admin.listingPage(listing.id),
            tooltipText: 'Admin Page of the Listing',
            icon: 'desktop',
          },
          {
            url: routes.admin.building(listing.building.id),
            tooltipText: 'Admin Page of the Building',
            icon: 'buildings-multiple',
          },
        ]}
        className={s.bartender}
      />
      {flags['sign-up-modal'] && <SignUpModal isOpen={signUpRequired} />}
      <div data-testid="listingLayout" className={s.grid}>
        <section className={s.rightContent}>
          <OnlyInMobile>
            <ListingLocation listing={listing} />
          </OnlyInMobile>
          <Gallery
            photos={listing.photos}
            video={listing.video}
            tourEmbedUrl={listing.tourEmbedUrl}
            isCurrentlyExclusive={listing.isCurrentlyExclusive}
            lightboxOpened={lightboxOpened}
            galleryPaginationClicked={galleryPaginationClicked}
            galleryThumbnailClicked={galleryThumbnailClicked}
            imagesZipUrl={listing.createImageZipUrl}
            onLightboxOpened={() => setIsLightboxOpen(true)}
            onLightboxClosed={() => setIsLightboxOpen(false)}
          />
          {listing.spaceProvider ? (
            <SpaceProvider spaceProvider={listing.spaceProvider} onClick={brandPageLinkClicked} />
          ) : null}
          <OnlyInMobile>
            <BasicInfo listing={listing} />
            <Financials
              leaseTermDropdownOpened={leaseTermDropdownOpened}
              leaseTermChanged={leaseTermChanged}
              baseRents={listing.baseRents}
              listingArea={listing.area}
            />
          </OnlyInMobile>
          {listing.description ? (
            <ExpandableDescription>{listing.description}</ExpandableDescription>
          ) : null}
          <Amenities amenities={listing.amenities} />
          <Rooms listing={listing} />
        </section>
        <section className={s.leftContent}>
          <OnlyInDesktopTablet>
            <ListingLocation listing={listing} />
            <BasicInfo listing={listing} />
            <Financials
              leaseTermDropdownOpened={leaseTermDropdownOpened}
              leaseTermChanged={leaseTermChanged}
              baseRents={listing.baseRents}
              listingArea={listing.area}
            />
          </OnlyInDesktopTablet>
          <ContactChooser
            isPreview={isPreview}
            listingAgents={listing.listingAgents}
            title={t('tourThisSpace')}
            emailSubjectName={[listing.buildingName, listing.name].join(' ')}
            trackContactClick={contactClicked}
            listingId={listing.id}
            hideLocation={listing.hideLocation}
          />
        </section>
      </div>
      <OnlyInDesktopTablet>
        <Divider />
      </OnlyInDesktopTablet>
      <VideoTour
        video={listing.video}
        tourEmbedUrl={listing.tourEmbedUrl}
        sourceContent={
          flags['media-analytics']
            ? PARAMETERS.spotlightTour
            : videoSourceContent[listing.virtualTourSpotlight]
        }
        sourcePage={PARAMETERS.listingPage}
        virtualTourSpotlight={listing.virtualTourSpotlight}
        isVisible={!isLightboxOpen}
      />
      <Divider className={classnames(!listing.video && !listing.tourEmbedUrl && s.hidden)} />
      <div className={s.locationContainer}>
        <Location
          address={listing.building.formattedAddress}
          latitude={listing.building.latitude}
          longitude={listing.building.longitude}
          hideLocation={listing.hideLocation}
        />
        <Transportation transitOptions={listing.building.transitOptions} />
      </div>
      {listing.hideLocation ? null : (
        <AboutBuilding
          viewBuildingClicked={viewBuildingClicked}
          building={listing.building}
          spaceProvider={listing.spaceProvider}
        />
      )}
    </>
  );
};

export default connector(Layout);
